/**
 * ConflictResolutionSheet — Bottom sheet for resolving sync conflicts.
 *
 * Shows each conflict (local vs cloud) and lets the user choose which version
 * to keep, or dismiss without acting (local version stays as-is).
 *
 * Props:
 *   isOpen            boolean
 *   onClose           () => void
 *   conflicts         { [entityId]: ConflictEntry }
 *   onKeepLocal       (entityId, entityType, localData) => void
 *   onKeepRemote      (entityId, entityType, remoteData) => void
 *   onDismissConflict (entityId) => void
 */

import React, { memo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CloudOff, Cloud, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import { haptic } from '../../shared/utils/haptics';

const CATEGORY_LABEL = {
  vital:    'Vital',
  work:     'Travail',
  health:   'Santé',
  personal: 'Personnel',
  social:   'Social',
  admin:    'Admin',
  chore:    'Corvée',
  pleasure: 'Plaisir',
  reward:   'Récompense',
};

export const ConflictResolutionSheet = memo(function ConflictResolutionSheet({
  isOpen,
  onClose,
  conflicts = {},
  onKeepLocal,
  onKeepRemote,
  onDismissConflict,
}) {
  const entries = Object.values(conflicts);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={`Conflits de synchronisation (${entries.length})`}
      size="large"
    >
      <div className="p-4 space-y-4 pb-safe">
        {entries.length === 0 ? (
          <div className="text-center py-10 text-zinc-500 text-sm">
            Aucun conflit en attente
          </div>
        ) : (
          <>
            <p className="text-xs text-zinc-500 leading-relaxed">
              Ces éléments ont été modifiés sur deux appareils différents. Choisissez quelle version garder.
            </p>

            {entries.map(conflict => (
              <ConflictCard
                key={conflict.entityId}
                conflict={conflict}
                onKeepLocal={() => {
                  haptic.medium();
                  onKeepLocal?.(conflict.entityId, conflict.entityType, conflict.local);
                }}
                onKeepRemote={() => {
                  haptic.medium();
                  onKeepRemote?.(conflict.entityId, conflict.entityType, conflict.remote);
                }}
                onDismiss={() => {
                  haptic.tap();
                  onDismissConflict?.(conflict.entityId);
                }}
              />
            ))}

            <button
              onClick={onClose}
              className="w-full py-3 text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              Fermer — conserver les versions locales
            </button>
          </>
        )}
      </div>
    </BottomSheet>
  );
});

// ─── Single conflict card ─────────────────────────────────────────────────────

const ConflictCard = memo(function ConflictCard({
  conflict,
  onKeepLocal,
  onKeepRemote,
  onDismiss,
}) {
  const [expanded, setExpanded] = useState(false);
  const { local, remote, detectedAt } = conflict;

  const title = local?.task || local?.title || remote?.task || remote?.title || conflict.entityId;
  const detectedDate = new Date(detectedAt).toLocaleString('fr-FR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="p-3 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
          <span className="text-amber-400 text-sm">⚡</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-100 truncate">{title}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">Détecté {detectedDate}</p>
        </div>
        <button
          onClick={() => setExpanded(e => !e)}
          className="p-1.5 text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Version comparison */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 grid grid-cols-2 gap-2">
              <VersionPanel
                label="Ma version"
                icon={<CloudOff className="w-3.5 h-3.5" />}
                iconColor="text-blue-400"
                bgColor="bg-blue-900/20 border-blue-700/30"
                item={local}
              />
              <VersionPanel
                label="Version cloud"
                icon={<Cloud className="w-3.5 h-3.5" />}
                iconColor="text-emerald-400"
                bgColor="bg-emerald-900/20 border-emerald-700/30"
                item={remote}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Actions */}
      <div className="px-3 pb-3 flex gap-2">
        <button
          onClick={onKeepLocal}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5
            bg-blue-600/20 hover:bg-blue-600/30 border border-blue-600/40
            text-blue-300 text-xs font-medium rounded-xl transition-all active:scale-[0.98]"
        >
          <CloudOff className="w-3.5 h-3.5" />
          Garder la mienne
        </button>
        <button
          onClick={onKeepRemote}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5
            bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-600/40
            text-emerald-300 text-xs font-medium rounded-xl transition-all active:scale-[0.98]"
        >
          <Cloud className="w-3.5 h-3.5" />
          Garder le cloud
        </button>
        <button
          onClick={onDismiss}
          className="p-2.5 bg-zinc-700/30 hover:bg-zinc-700/60 text-zinc-500
            hover:text-zinc-300 rounded-xl transition-all active:scale-[0.98]"
          title="Ignorer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
});

// ─── Version detail panel ─────────────────────────────────────────────────────

const VersionPanel = memo(function VersionPanel({ label, icon, iconColor, bgColor, item }) {
  if (!item) return null;

  const rows = [
    item.task   && ['Tâche',    item.task],
    item.title  && ['Titre',    item.title],
    item.category && ['Catégorie', CATEGORY_LABEL[item.category] ?? item.category],
    item.date   && ['Date',     item.date],
    item.time   && ['Heure',    item.time],
    item.updatedAt && ['Modifié', new Date(item.updatedAt).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    })],
    item.version !== undefined && ['Version', `v${item.version}`],
  ].filter(Boolean);

  return (
    <div className={`rounded-xl border p-2.5 ${bgColor}`}>
      <div className={`flex items-center gap-1 mb-2 ${iconColor}`}>
        {icon}
        <span className="text-[10px] font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <div className="space-y-1">
        {rows.map(([key, value]) => (
          <div key={key}>
            <span className="text-[9px] text-zinc-500 uppercase tracking-wider block">{key}</span>
            <span className="text-[11px] text-zinc-200 leading-tight break-words">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

export default ConflictResolutionSheet;
